/**
 * Integrations → Incoming (`?tab=incoming`; the directory and the symbols here
 * still say "simulation", which was the tab's original name). The raw HL7
 * packets going in and out of the data layer, for the MedStar tunnel test: the
 * acceptance criterion is showing the message arrived, not a populated form.
 *
 * Two modes, Receive and Send, driven by `?sim=` so both are deep-linkable.
 *
 * The mode switch is a segmented control rather than a nested <Tabs>, and that
 * is load-bearing: Radix renders the active TabsContent with role="tabpanel",
 * and the e2e helpers (`flows/integrations.flow.ts`, `pages/integrations.page.ts`)
 * locate it with an unscoped, singular `getByRole('tabpanel')`. A second Tabs
 * here would put two tabpanels in the DOM at once and every one of those
 * locators would fail Playwright's strict mode. It is also the more honest
 * control: Receive and Send share the status strip and channel table, so this is
 * a mode within one panel, not a second level of navigation.
 *
 * Polling lifecycle is free: Radix unmounts an inactive TabsContent
 * (`children: present && children`), so leaving the tab destroys this component
 * and React Query drops the interval with it. Never add `forceMount` to that
 * TabsContent — the poll would keep running invisibly.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Loader2, Radio, RefreshCw, Send, Waypoints } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { DataLayerError } from '@/lib/dataLayer/errors'
import { setDataLayerSourceId } from '@/lib/dataLayerBase'
import { useDataLayerSourceId } from '@/hooks/useDataLayerSourceId'
import { useDataLayerSources } from '@/hooks/useDataLayerSources'
import {
  dataLayerOverviewKey,
  useDataLayerOverview,
  DATA_LAYER_POLL_MS,
} from '@/hooks/useDataLayerOverview'
import { ChannelTable } from './ChannelTable'
import { DataLayerSourceChrome, type SourceChromeState } from './DataLayerSourceChrome'
import { DataLayerStatusStrip } from './DataLayerStatusStrip'
import { DataLayerUnavailable } from './DataLayerUnavailable'
import { ReceivePanel } from './ReceivePanel'
import { SendPanel } from './SendPanel'

export const SIM_VALUES = ['receive', 'send'] as const
export type SimMode = (typeof SIM_VALUES)[number]

export function isSimMode(value: string | null): value is SimMode {
  return value !== null && (SIM_VALUES as readonly string[]).includes(value)
}

export function SimulationPanel({
  mode,
  onModeChange,
}: {
  mode: SimMode
  onModeChange: (mode: SimMode) => void
}) {
  const queryClient = useQueryClient()
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.integrationsManage)
  const [channelsOpen, setChannelsOpen] = useState(false)

  // The allowlist first; every poll is gated on a resolved source id, so the
  // tab never fires a packet request before it knows where it is pointed.
  const sourcesQuery = useDataLayerSources()
  const sourceId = useDataLayerSourceId()

  const { data, error, isLoading } = useDataLayerOverview(sourceId !== null)

  function refresh() {
    queryClient.invalidateQueries({ queryKey: dataLayerOverviewKey })
  }

  /**
   * Switching a source must REMOVE the cached queries, not invalidate them:
   * the tail keeps a `sinceSeq` cursor in the cache and `seq` is monotonic per
   * harness process, so carrying one source's cursor to another either hides
   * every message or replays the log. The sources list itself lives under a
   * different key on purpose, so it survives the sweep.
   */
  function selectSource(id: string) {
    if (id === sourceId) return
    setDataLayerSourceId(id)
    queryClient.removeQueries({ queryKey: ['data-layer'] })
  }

  const overviewFailure = error instanceof DataLayerError ? error.failure : null
  const sourcesFailure =
    sourcesQuery.error instanceof DataLayerError ? sourcesQuery.error.failure : null
  const failure = sourcesFailure ?? overviewFailure

  const chromeState: SourceChromeState =
    failure !== null ? 'unreachable' : data !== undefined ? 'confirmed' : 'waiting'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Waypoints className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold leading-tight">Data layer</h3>
            <p className="text-xs text-muted-foreground">
              Raw HL7 in and out of the MLLP harness.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Always visible, not tucked into a menu: a screenshot of a demo has
              to say which source produced it, in the source's own words. */}
          <DataLayerSourceChrome
            sources={sourcesQuery.data?.sources}
            sourceId={sourceId}
            deployment={data?.status.deployment ?? null}
            state={chromeState}
            canManage={canManage}
            onSelect={selectSource}
          />
          {/* Honest about the poll: it halts in the error state (see
              useDataLayerOverview) and resumes on Retry or a source switch. */}
          <span className="text-[11px] text-muted-foreground">
            {failure !== null
              ? 'Paused until retry'
              : `Updates every ${(DATA_LAYER_POLL_MS / 1000).toFixed(1)}s`}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground opacity-60 hover:opacity-100"
            title="Refresh"
            onClick={refresh}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {failure && <DataLayerUnavailable failure={failure} sourceId={sourceId} onRetry={refresh} />}

      {isLoading && !data && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          <DataLayerStatusStrip status={data.status} />

          <div className="rounded-lg border border-border/60">
            <button
              type="button"
              onClick={() => setChannelsOpen((open) => !open)}
              aria-expanded={channelsOpen}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left"
            >
              <span className="text-sm font-medium">
                MLLP channels
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {data.channels.length}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  channelsOpen && 'rotate-180',
                )}
              />
            </button>
            {channelsOpen && (
              <div className="border-t border-border/60 p-1">
                <ChannelTable channels={data.channels} />
              </div>
            )}
          </div>

          <ModeSwitch mode={mode} onModeChange={onModeChange} />

          {/* Only the active mode is mounted, so the inactive one's poll stops
              with it — the same mechanism that stops everything on tab-away. */}
          {mode === 'receive' ? (
            <ReceivePanel channels={data.channels} />
          ) : (
            <SendPanel channels={data.channels} />
          )}
        </>
      )}
    </div>
  )
}

const MODE_LABEL: Record<SimMode, { label: string; icon: React.ElementType; hint: string }> = {
  receive: { label: 'Receive', icon: Radio, hint: 'Inbound HL7 and the ACKs we returned' },
  send: { label: 'Send', icon: Send, hint: 'Send a message out and show the ACK' },
}

function ModeSwitch({
  mode,
  onModeChange,
}: {
  mode: SimMode
  onModeChange: (mode: SimMode) => void
}) {
  return (
    <div
      role="group"
      aria-label="Incoming mode"
      className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-muted/40 p-1"
    >
      {SIM_VALUES.map((value) => {
        const { label, icon: Icon, hint } = MODE_LABEL[value]
        const active = mode === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={hint}
            onClick={() => onModeChange(value)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}
